import { createColumnHelper } from "@tanstack/react-table";
import {
  challanItemsTable,
  incomingItemsTable,
  pendingItemsTable,
} from "../../interfaces/app_types";
import { getDateFromString, getDateFromStringYear } from "../funcs/time";
import { fontColorBasedonBg, hexToRgba } from "../funcs/common";
import IncomingHistoryButton from "../../components/incoming-items/IncomingHistoryButton";
import { getDaysSinceDate } from "../funcs/days";
import IncomingChallanItemsButton from "../../components/incoming-items/IncomingChallanItemsButton";
import {
  deleteChallan,
  getIncomingChallanPDF,
  lockChallan,
} from "../../api/incoming";
import InventoryAddDeleteButton from "../../components/inventory/InventoryEditDeleteButton";
import MutationButton from "../../components/inventory/MutationButton";
import IncomingBalanceButton from "../../components/inventory/IncomingBalanceButton";

const columnHelper = createColumnHelper<incomingItemsTable>();

const getIncomingColors = (days: number) => {
  const greenColor = "#4eab64ff"; // Bootstrap success color
  const redColor = "#dc3545"; // Bootstrap danger color
  const orangeColor = "#fd7e14"; // Bootstrap warning color for medium risk
  const blackColor = "#000000"; // Black for dark backgrounds
  const yellowColor = "#ffc107"; // Bootstrap warning color
  let currentColor = "#FFFFFF"; // Default color for light backgrounds

  if (days <= 10 && days > 5) {
    currentColor = greenColor;
  } else if (days <= 10) {
    currentColor = yellowColor;
  } else if (days <= 15) {
    currentColor = orangeColor;
  } else if (days <= 20) {
    currentColor = redColor;
  } else {
    currentColor = blackColor;
  }

  return {
    "--item-background-color": hexToRgba(currentColor, 0.7),
    "--item-color": fontColorBasedonBg(currentColor),
  };
};

export const recdColumns = [
  columnHelper.accessor((row) => row?.incoming_id, {
    header: "ID",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.part_name, {
    header: "Part Name",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing, {
    id: "Drawing",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing_link, {
    id: "Link",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() !== "" ? (
        <a href={info.getValue()} target="_blank" rel="noreferrer">
          <i className="bi bi-box-arrow-up-right"></i>
        </a>
      ) : (
        "NA"
      ),
  }),
  columnHelper.accessor((row) => row?.quantity, {
    id: "Qty",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.action_date, {
    id: "Date",
    enableColumnFilter: false,
    cell: (info) => getDateFromString(info.getValue()),
  }),
  columnHelper.accessor((row) => row?.party_name, {
    id: "Party",
    cell: (info) => (
      <div
        className="custom-color-label parties-label"
        style={
          {
            "--item-background-color": hexToRgba(
              info.row.original?.party_color,
              0.7
            ),
            "--item-color": fontColorBasedonBg(info.row.original?.party_color),
          } as React.CSSProperties
        }
      >
        {info.getValue()}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.remarks, {
    id: "Remarks",
    enableHiding: true,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.incoming_id, {
    id: "Edit?",
    enableColumnFilter: false,
    cell: (info) =>
      info.row.original?.locked !== true ? (
        <InventoryAddDeleteButton
          featureType={"incoming"}
          btnType="edit"
          part_id={info.getValue()}
        />
      ) : (
        "NA"
      ),
  }),
  columnHelper.accessor((row) => row?.incoming_id, {
    id: "Delete?",
    enableColumnFilter: false,
    cell: (info) =>
      info.row.original?.locked !== true ? (
        <InventoryAddDeleteButton
          featureType={"incoming"}
          btnType="delete"
          part_id={info.getValue()}
        />
      ) : (
        "NA"
      ),
  }),
];

export const sentColumns = [
  columnHelper.accessor((row) => row?.incoming_id, {
    header: "ID",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.part_name, {
    header: "Part Name",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing, {
    id: "Drawing",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing_link, {
    id: "Link",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() !== "" ? (
        <a href={info.getValue()} target="_blank" rel="noreferrer">
          <i className="bi bi-box-arrow-up-right"></i>
        </a>
      ) : (
        "NA"
      ),
  }),
  columnHelper.accessor((row) => row?.quantity, {
    id: "Qty",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.action_date, {
    id: "Date",
    enableColumnFilter: false,
    cell: (info) => getDateFromString(info.getValue()),
  }),
  columnHelper.accessor((row) => row?.party_name, {
    id: "Party",
    cell: (info) => (
      <div
        className="custom-color-label parties-label"
        style={
          {
            "--item-background-color": hexToRgba(
              info.row.original?.party_color,
              0.7
            ),
            "--item-color": fontColorBasedonBg(info.row.original?.party_color),
          } as React.CSSProperties
        }
      >
        {info.getValue()}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.challan_number, {
    id: "Challan No",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.remarks, {
    id: "Remarks",
    enableHiding: true,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.incoming_id, {
    id: "Edit?",
    enableColumnFilter: false,
    cell: (info) =>
      info.row.original?.locked !== true ? (
        <InventoryAddDeleteButton
          featureType={"incoming"}
          btnType="edit"
          part_id={info.getValue()}
        />
      ) : (
        "-"
      ),
  }),
  columnHelper.accessor((row) => row?.incoming_id, {
    id: "Delete?",
    enableColumnFilter: false,
    cell: (info) =>
      info.row.original?.locked !== true ? (
        <InventoryAddDeleteButton
          featureType={"incoming"}
          btnType="delete"
          part_id={info.getValue()}
        />
      ) : (
        "-"
      ),
  }),
];

export const sentRecdHistoryColumns = [
  columnHelper.accessor((row) => row?.incoming_id, {
    header: "ID",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.part_name, {
    header: "Part Name",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing, {
    id: "Drawing",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing_link, {
    id: "Link",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() !== "" ? (
        <a href={info.getValue()} target="_blank" rel="noreferrer">
          <i className="bi bi-box-arrow-up-right"></i>
        </a>
      ) : (
        "NA"
      ),
  }),
  columnHelper.accessor((row) => row?.status, {
    id: "Status",
    enableColumnFilter: false,
    cell: (info) => (
      <div className={`styled-btn--${info.getValue() === 0 ? "sent" : "recd"}`}>
        {info.getValue() === 0 ? "SENT" : "RECD"}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.quantity, {
    id: "Qty",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.action_date, {
    id: "Date",
    enableColumnFilter: false,
    cell: (info) => getDateFromStringYear(info.getValue()),
  }),
  columnHelper.accessor((row) => row?.party_name, {
    id: "Party",
    cell: (info) => (
      <div
        className="custom-color-label parties-label"
        style={
          {
            "--item-background-color": hexToRgba(
              info.row.original?.party_color,
              0.7
            ),
            "--item-color": fontColorBasedonBg(info.row.original?.party_color),
          } as React.CSSProperties
        }
      >
        {info.getValue()}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.remarks, {
    id: "Remarks",
    cell: (info) => info.getValue(),
  }),
];

const pendingColumnHelper = createColumnHelper<pendingItemsTable>();

export const pendingColumns = [
  pendingColumnHelper.accessor((row) => row?.part_id, {
    header: "Part ID",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  pendingColumnHelper.accessor((row) => row?.part_name, {
    header: "Part Name",
    cell: (info) => info.getValue(),
  }),
  pendingColumnHelper.accessor((row) => row?.drawing, {
    id: "Drawing",
    cell: (info) => info.getValue(),
  }),
  pendingColumnHelper.accessor((row) => row?.drawing_link, {
    id: "Link",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() !== "" ? (
        <a href={info.getValue()} target="_blank" rel="noreferrer">
          <i className="bi bi-box-arrow-up-right"></i>
        </a>
      ) : (
        "NA"
      ),
  }),
  pendingColumnHelper.accessor((row) => row?.pending_qty, {
    id: "Pending Qty",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  pendingColumnHelper.accessor((row) => row?.part_id, {
    id: "BALANCE",
    enableColumnFilter: false,
    cell: (info) => (
      <IncomingBalanceButton
        part_id={info.getValue()}
        type="recd"
        party_id={info.row.original?.party_id}
        drawing={info.row.original?.drawing}
        quantity={info.row.original?.pending_qty}
      />
    ),
  }),
  pendingColumnHelper.accessor((row) => row?.party_name, {
    id: "Party",
    cell: (info) => (
      <div
        className="custom-color-label parties-label"
        style={
          {
            "--item-background-color": hexToRgba(
              info.row.original?.party_color,
              0.7
            ),
            "--item-color": fontColorBasedonBg(info.row.original?.party_color),
          } as React.CSSProperties
        }
      >
        {info.getValue()}
      </div>
    ),
  }),
  pendingColumnHelper.accessor(
    (row) => {
      return getDaysSinceDate(row?.last_sent_date);
    },
    {
      id: "Days Sent",
      enableColumnFilter: false,
      cell: (info) => (
        <div
          className="custom-color-label"
          style={getIncomingColors(info.getValue() || 0) as React.CSSProperties}
        >
          {info.getValue() ? info.getValue() : "NA"}
        </div>
      ),
    }
  ),
  pendingColumnHelper.accessor(
    (row) => {
      return getDaysSinceDate(row?.last_recd_date);
    },
    {
      id: "Days Recd",
      enableColumnFilter: false,
      cell: (info) => (
        <div
          className="custom-color-label"
          style={getIncomingColors(info.getValue() || 0) as React.CSSProperties}
        >
          {info.getValue() ? info.getValue() : "NA"}
        </div>
      ),
    }
  ),

  pendingColumnHelper.accessor((row) => row?.part_id, {
    id: "History",
    enableColumnFilter: false,
    cell: (props) => (
      <IncomingHistoryButton
        modalId={"#historyIncomingModal"}
        partId={props.row.original?.part_id}
        partyId={props.row.original?.party_id}
        modalTitle={`${props.row.original?.party_name} : ${props.row.original?.part_name} `}
      />
    ),
  }),
];

const challanColumnHelper = createColumnHelper<challanItemsTable>();

export const challanColumns = [
  challanColumnHelper.accessor((row) => row?.challan_id, {
    header: "ID",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  challanColumnHelper.accessor((row) => row?.challan_number, {
    header: "Challan Name",
    cell: (info) => info.getValue(),
  }),
  challanColumnHelper.accessor((row) => row?.challan_date, {
    id: "Date",
    enableColumnFilter: false,
    cell: (info) => getDateFromString(info.getValue()),
  }),
  challanColumnHelper.accessor((row) => row?.party_name, {
    id: "Party",
    cell: (info) => (
      <div
        className="custom-color-label parties-label"
        style={
          {
            "--item-background-color": hexToRgba(
              info.row.original?.party_color,
              0.7
            ),
            "--item-color": fontColorBasedonBg(info.row.original?.party_color),
          } as React.CSSProperties
        }
      >
        {info.getValue()}
      </div>
    ),
  }),
  challanColumnHelper.accessor((row) => row?.company, {
    id: "Company",
    enableColumnFilter: false,
    cell: (info) => (
      <div
        className="custom-color-label parties-label"
        style={
          {
            "--item-background-color": hexToRgba(
              info.getValue() === "PF" ? "#0a408be6" : "#7b4409ff",
              0.7
            ),
            "--item-color": info.getValue() === "PF" ? "#FFFFFF" : "#000000",
          } as React.CSSProperties
        }
      >
        {info.getValue() === "PF" ? "Pressco-Fab" : "Press-Tech"}
      </div>
    ),
  }),
  challanColumnHelper.accessor((row) => row?.challan_id, {
    id: "View Items",
    enableColumnFilter: false,
    cell: (props) =>
      props.row.original?.is_deleted !== true ? (
        <IncomingChallanItemsButton
          modalId={"#historyIncomingModal"}
          challanId={props.getValue()}
          modalTitle={`${props.row.original?.party_name} : ${props.row.original?.challan_id} `}
        />
      ) : (
        ""
      ),
  }),
  challanColumnHelper.accessor((row) => row?.challan_id, {
    id: "Download",
    enableColumnFilter: false,
    cell: (props) =>
      props.row.original?.is_deleted !== true ? (
        <button
          className="btn"
          onClick={async () => await getIncomingChallanPDF(props.getValue())}
        >
          <i className="bi bi-download"></i>
        </button>
      ) : (
        ""
      ),
  }),
  challanColumnHelper.accessor((row) => row?.challan_id, {
    id: "Lock?",
    enableColumnFilter: false,
    cell: (info) =>
      info.row.original?.locked !== true &&
      info.row.original?.is_deleted !== true ? (
        <MutationButton
          id={info.getValue()}
          type="delete"
          deleteFn={lockChallan}
          mutationMessage="Challan Locked Successfully"
          symbol={"unlock"}
          roles={["admin", "accounts"]}
          invalidateKeys={[
            "incoming-challan",
            "incoming-sent",
            "incoming-recd",
            "incoming-pending",
          ]}
        />
      ) : (
        <div className="btn pe-none">
          <i
            className={`bi ${
              info.row.original?.is_deleted !== true ? "bi-lock-fill" : ""
            } d-flex justify-content-center`}
          ></i>
        </div>
      ),
  }),
  challanColumnHelper.accessor((row) => row?.challan_id, {
    id: "Delete?",
    enableColumnFilter: false,
    cell: (info) =>
      info.row.original?.is_deleted !== true &&
      info.row.original?.locked !== true ? (
        <MutationButton
          id={info.getValue()}
          type="delete"
          deleteFn={deleteChallan}
          roles={["admin", "accounts"]}
          color="danger"
          symbol="x-lg"
          invalidateKeys={["incoming-challan"]}
        />
      ) : (
        <div className="btn pe-none">
          <i
            className={`bi bi-${
              info.row.original?.locked
                ? "check-lg text-success"
                : "x-square text-danger"
            } `}
          ></i>
        </div>
      ),
  }),
];
